<script setup lang="ts">
import { computed, reactive, ref } from "vue";
import { AlertOctagon, ArrowRight, EyeOff, Plus, X } from "lucide-vue-next";
import { useWorkspaceStore } from "@/stores/workspace";
import { shortDate } from "@/utils/dates";
import type { Visibility } from "@/types/models";

const workspace = useWorkspaceStore();
const createOpen = ref(false);
const selectedId = ref("");
const filter = ref<"all" | "issue" | "risk">("all");
const saving = ref(false);
const error = ref("");
const form = reactive<{
  kind: "risk" | "issue";
  title: string;
  impactNarrative: string;
  neededDecision: string;
  severity: "low" | "medium" | "high" | "critical";
  likelihood: "unlikely" | "possible" | "likely";
  impact: "low" | "medium" | "high";
  dueDate: string;
  visibility: Visibility;
}>({
  kind: "risk",
  title: "",
  impactNarrative: "",
  neededDecision: "",
  severity: "medium",
  likelihood: "possible",
  impact: "medium",
  dueDate: "",
  visibility: "Shared",
});
const visibleRisks = computed(() =>
  (workspace.active?.risks || []).filter(
    (item) =>
      filter.value === "all" || item.kind.toLowerCase() === filter.value,
  ),
);

async function create() {
  if (!form.title.trim() || !form.impactNarrative.trim()) return;
  saving.value = true;
  error.value = "";
  try {
    await workspace.createRisk({
      ...form,
      title: form.title.trim(),
      impactNarrative: form.impactNarrative.trim(),
      neededDecision: form.neededDecision.trim(),
      dueDate: form.dueDate || null,
    });
    Object.assign(form, {
      kind: "risk",
      title: "",
      impactNarrative: "",
      neededDecision: "",
      severity: "medium",
      likelihood: "possible",
      impact: "medium",
      dueDate: "",
      visibility: "Shared",
    });
    createOpen.value = false;
  } catch (createError) {
    error.value =
      createError instanceof Error
        ? createError.message
        : "The issue or risk could not be added.";
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <div class="risks-page">
    <header>
      <div>
        <h2>Issues and risks</h2>
        <p>Track what is blocking delivery now and what may affect it later.</p>
      </div>
      <button
        v-if="workspace.isInternal"
        class="button button--primary"
        type="button"
        @click="createOpen = true"
      >
        <Plus :size="16" /> Add issue or risk
      </button>
    </header>
    <div class="risk-toolbar">
      <div class="risk-summary">
        <span
          ><strong>{{
            workspace.active?.risks.filter(
              (item) => item.kind === "Issue" && item.state !== "Resolved",
            ).length
          }}</strong>
          open issues</span
        ><span
          ><strong>{{
            workspace.active?.risks.filter(
              (item) => item.kind === "Risk" && item.state !== "Resolved",
            ).length
          }}</strong>
          active risks</span
        ><span
          ><strong>{{
            workspace.active?.risks.filter((item) => item.impact === "High")
              .length
          }}</strong>
          high impact</span
        >
      </div>
      <label class="risk-filter">
        <span class="sr-only">Filter issues and risks</span>
        <select v-model="filter" class="select">
          <option value="all">All issues and risks</option>
          <option value="issue">Issues only</option>
          <option value="risk">Risks only</option>
        </select>
      </label>
    </div>
    <section
      class="risk-sheet"
      :class="{ 'risk-sheet--client': !workspace.isInternal }"
    >
      <div class="risk-head">
        <span>Issue or risk</span><span v-if="workspace.isInternal">Impact</span
        ><span>Owner</span><span>Decision due</span><span>Status</span
        ><span></span>
      </div>
      <button
        v-for="item in visibleRisks"
        :key="item.id"
        class="risk-row"
        :class="{ 'risk-row--internal': item.visibility === 'Internal' }"
        type="button"
        :aria-expanded="selectedId === item.id"
        @click="selectedId = selectedId === item.id ? '' : item.id"
      >
        <span class="risk-title"
          ><span class="risk-icon" :class="item.kind === 'Issue' ? 'issue' : ''"
            ><AlertOctagon :size="18" /></span
          ><span
            ><strong
              >{{ item.title }}
              <span
                v-if="workspace.isInternal && item.visibility === 'Internal'"
                class="internal-marker"
                title="Internal"
                ><EyeOff :size="13" aria-hidden="true" /><span class="sr-only"
                  >Internal</span
                ></span
              ></strong
            ><small>{{ item.kind }} · {{ item.linkedTask }}</small></span
          ></span
        ><span v-if="workspace.isInternal"
          ><span
            class="badge"
            :class="item.impact === 'High' ? 'badge--blocked' : 'badge--risk'"
            >{{ item.impact }}</span
          ></span
        ><span>{{ item.owner }}</span
        ><span class="tabular">{{ shortDate(item.dueDate) }}</span
        ><span>{{ item.state }}</span
        ><ArrowRight :size="16" />
      </button>
      <article
        v-for="item in visibleRisks.filter(
          (candidate) => candidate.id === selectedId,
        )"
        :key="`${item.id}-detail`"
        class="risk-detail"
      >
        <strong>Impact and response</strong>
        <p>{{ item.mitigation || "No response has been recorded yet." }}</p>
        <span>{{ item.linkedTask }}</span>
      </article>
    </section>
    <div v-if="createOpen" class="risk-modal">
      <button
        class="risk-scrim"
        type="button"
        aria-label="Close issue or risk form"
        @click="createOpen = false"
      ></button>
      <form @submit.prevent="create">
        <header>
          <div>
            <h2>Add issue or risk</h2>
            <p>Record the impact, decision, owner, and access.</p>
          </div>
          <button
            class="icon-button"
            type="button"
            aria-label="Close"
            @click="createOpen = false"
          >
            <X :size="18" />
          </button>
        </header>
        <p v-if="error" class="form-error" role="alert">{{ error }}</p>
        <div class="form-row">
          <div class="field">
            <label for="risk-kind">Type</label
            ><select id="risk-kind" v-model="form.kind" class="select">
              <option value="risk">Risk</option>
              <option value="issue">Issue</option>
            </select>
          </div>
          <label class="internal-risk-option" for="risk-internal">
            <input
              id="risk-internal"
              type="checkbox"
              :checked="form.visibility === 'Internal'"
              @change="
                form.visibility = ($event.target as HTMLInputElement).checked
                  ? 'Internal'
                  : 'Shared'
              "
            />
            <span
              ><strong>Internal</strong
              ><small
                >Only eligible VIPdesk project participants can see it.</small
              ></span
            >
          </label>
        </div>
        <div class="field">
          <label for="risk-title">Title</label
          ><input
            id="risk-title"
            v-model="form.title"
            class="input"
            required
            maxlength="240"
          />
        </div>
        <div class="field">
          <label for="risk-impact">Impact narrative</label
          ><textarea
            id="risk-impact"
            v-model="form.impactNarrative"
            class="textarea"
            required
            placeholder="What could happen and why it matters"
          ></textarea>
        </div>
        <div class="field">
          <label for="risk-decision">Needed decision or response</label
          ><textarea
            id="risk-decision"
            v-model="form.neededDecision"
            class="textarea"
            placeholder="What needs to happen next"
          ></textarea>
        </div>
        <div class="form-row">
          <div class="field">
            <label for="risk-level">Impact</label
            ><select id="risk-level" v-model="form.impact" class="select">
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
          <div class="field">
            <label for="risk-likelihood">Likelihood</label
            ><select
              id="risk-likelihood"
              v-model="form.likelihood"
              class="select"
            >
              <option value="unlikely">Unlikely</option>
              <option value="possible">Possible</option>
              <option value="likely">Likely</option>
            </select>
          </div>
          <div class="field">
            <label for="risk-due">Decision due</label
            ><input
              id="risk-due"
              v-model="form.dueDate"
              class="input"
              type="date"
            />
          </div>
        </div>
        <footer>
          <button
            class="button button--secondary"
            type="button"
            @click="createOpen = false"
          >
            Cancel</button
          ><button
            class="button button--primary"
            type="submit"
            :disabled="
              saving || !form.title.trim() || !form.impactNarrative.trim()
            "
          >
            {{ saving ? "Adding…" : "Add issue or risk" }}
          </button>
        </footer>
      </form>
    </div>
  </div>
</template>

<style scoped>
.risks-page {
  height: 100%;
  overflow: auto;
  padding: 24px 28px 50px;
  background: var(--paper);
}
.risks-page > header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 18px;
}
.risks-page header p {
  margin-top: 4px;
  color: var(--text-soft);
}
.risk-toolbar {
  min-height: 58px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}
.risk-summary {
  display: flex;
  gap: 20px;
  color: var(--text-soft);
}
.risk-summary span + span {
  padding-left: 20px;
  border-left: 1px solid var(--rule-strong);
}
.risk-summary strong {
  color: var(--text);
}
.risk-filter {
  width: 190px;
}
.risk-sheet {
  overflow: hidden;
  border: 1px solid var(--rule);
  border-radius: 7px;
  background: white;
}
.risk-head,
.risk-row {
  display: grid;
  grid-template-columns: minmax(300px, 1.8fr) 78px 120px 100px 80px 20px;
  align-items: center;
  gap: 13px;
  padding: 0 15px;
}
.risk-sheet--client .risk-head,
.risk-sheet--client .risk-row {
  grid-template-columns: minmax(300px, 1.8fr) 120px 100px 80px 20px;
}
.risk-head {
  min-height: 36px;
  border-bottom: 1px solid var(--rule);
  background: var(--sheet-subtle);
  color: var(--text-faint);
  font-size: 10px;
  font-weight: 750;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}
.risk-row {
  width: 100%;
  min-height: 70px;
  border-bottom: 1px solid var(--rule);
  background: white;
  text-align: left;
  cursor: pointer;
}
.risk-row:last-child {
  border-bottom: 0;
}
.risk-row:hover {
  background: var(--sheet-subtle);
}
.risk-row--internal {
  background: color-mix(in srgb, var(--internal-soft) 46%, white);
}
.internal-marker {
  display: inline-flex;
  margin-left: 5px;
  color: var(--internal);
  vertical-align: -2px;
}
.internal-risk-option {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  padding-top: 23px;
}
.internal-risk-option input {
  margin-top: 3px;
}
.internal-risk-option span {
  display: grid;
  gap: 2px;
}
.internal-risk-option small {
  color: var(--text-soft);
}
.risk-title {
  display: flex;
  align-items: center;
  gap: 10px;
}
.risk-title > span:last-child {
  display: grid;
}
.risk-title small {
  color: var(--text-soft);
}
.risk-icon {
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  color: var(--warning);
  background: var(--warning-soft);
}
.risk-icon.issue {
  color: var(--danger);
  background: var(--danger-soft);
}
.risk-detail {
  padding: 16px 24px 18px 60px;
  border-bottom: 1px solid var(--rule);
  background: var(--sheet-subtle);
}
.risk-detail p {
  margin: 5px 0;
}
.risk-detail span {
  color: var(--text-soft);
  font-size: 12px;
}
.risk-modal {
  position: fixed;
  z-index: 100;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 16px;
}
.risk-scrim {
  position: absolute;
  inset: 0;
  border: 0;
  background: rgba(13, 35, 50, 0.48);
}
.risk-modal > form {
  position: relative;
  width: min(680px, 100%);
  max-height: calc(100vh - 32px);
  overflow: auto;
  display: grid;
  gap: 15px;
  padding: 22px;
  border-radius: 8px;
  background: white;
  box-shadow: var(--shadow-panel);
}
.risk-modal header,
.risk-modal footer {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}
.risk-modal header p {
  color: var(--text-soft);
}
.risk-modal footer {
  justify-content: flex-end;
}
.form-error {
  padding: 9px 11px;
  border: 1px solid var(--danger);
  border-radius: 5px;
  background: var(--danger-soft);
  color: var(--danger);
}
@media (max-width: 900px) {
  .risk-head,
  .risk-row {
    grid-template-columns: minmax(260px, 1.5fr) 80px 120px 90px 20px;
  }
  .risk-sheet--client .risk-head,
  .risk-sheet--client .risk-row {
    grid-template-columns: minmax(260px, 1.5fr) 120px 90px 20px;
  }
  .risk-head span:nth-child(4),
  .risk-row > span:nth-child(4),
  .risk-sheet--client .risk-head span:nth-child(3),
  .risk-sheet--client .risk-row > span:nth-child(3) {
    display: none;
  }
}
@media (max-width: 600px) {
  .risks-page {
    padding: 18px 14px 40px;
  }
  .risks-page > header {
    flex-direction: column;
  }
  .risk-toolbar {
    align-items: stretch;
    flex-direction: column;
    padding: 12px 0;
  }
  .risk-summary {
    justify-content: space-between;
  }
  .risk-summary span + span {
    padding-left: 0;
    border: 0;
  }
  .risk-head {
    display: none;
  }
  .risk-row {
    grid-template-columns: 1fr auto;
    padding: 12px;
  }
  .risk-row > span:nth-child(2),
  .risk-row > span:nth-child(3),
  .risk-row > span:nth-child(5) {
    display: none;
  }
  .risk-row > svg {
    grid-column: 2;
    grid-row: 1;
  }
}
</style>
